<template>
  <div class="modal fade" id="modalProgramarRecurrente" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content divisan-modal border-0">
        <div class="modal-header border-0 pb-0">
          <h5 class="fw-bold text-cafe">
            <i class="bi bi-arrow-repeat me-2"></i>Programar mantenimiento recurrente
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="store.progresoRecurrente.activo"
          ></button>
        </div>

        <div class="modal-body">
          <!-- ── PASO 1: Configuración ── -->
          <div v-if="paso === 1">
            <div class="row g-3">
              <!-- Máquina + Fecha de inicio en el mismo renglón -->
              <div class="col-12 col-md-6">
                <label class="form-label label-divisan">Máquina</label>
                <div class="custom-select-wrapper" v-click-outside="cerrarDropMaquina">
                  <div
                    class="form-control divisan-input-modal custom-select-input select-display"
                    :class="{ 'is-placeholder': !form.maquina }"
                    @click="dropMaquina = !dropMaquina"
                  >
                    <span v-if="form.maquina">{{
                      maquinas.find((m) => m.id === form.maquina)?.nombre
                    }}</span>
                    <span v-else class="placeholder-text">Selecciona una máquina</span>
                  </div>
                  <i
                    class="bi custom-select-arrow"
                    :class="dropMaquina ? 'bi-chevron-up' : 'bi-chevron-down'"
                    @mousedown.prevent="dropMaquina = !dropMaquina"
                  ></i>
                  <div v-if="dropMaquina" class="custom-dropdown">
                    <div
                      v-for="m in maquinas"
                      :key="m.id"
                      class="custom-dropdown-item"
                      :class="{ selected: form.maquina === m.id }"
                      @mousedown.prevent="elegirMaquina(m.id)"
                    >
                      {{ m.nombre }}
                    </div>
                    <div v-if="!maquinas.length" class="custom-dropdown-item text-muted">
                      Sin máquinas disponibles
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label label-divisan">Fecha de inicio</label>
                <input
                  v-model="form.fechaInicio"
                  type="date"
                  :min="hoyStr"
                  :max="finAnioStr"
                  class="form-control divisan-input-modal"
                />
              </div>

              <!-- Rutinas — multi-select -->
              <div class="col-12">
                <label class="form-label label-divisan">
                  Rutinas
                  <span class="ms-2 text-muted fw-normal" style="font-size: 0.78rem"
                    >(una o más)</span
                  >
                </label>

                <!-- Buscador -->
                <div class="search-rutina mb-2">
                  <i class="bi bi-search"></i>
                  <input
                    v-model="busquedaRutina"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Buscar rutina..."
                    style="padding-left: 2rem"
                  />
                </div>

                <div class="rutinas-lista">
                  <div
                    v-for="r in rutinasFiltradas"
                    :key="r.id"
                    class="rutina-check-row"
                    :class="{ seleccionada: form.rutinas.includes(r.id) }"
                    @click="toggleRutina(r.id)"
                  >
                    <div class="rutina-check-box">
                      <i
                        :class="
                          form.rutinas.includes(r.id)
                            ? 'bi bi-check-square-fill'
                            : 'bi bi-square'
                        "
                      ></i>
                    </div>
                    <div class="rutina-check-info flex-grow-1">
                      <span class="fw-bold" style="font-size: 0.88rem; color: var(--d-brown)">{{
                        r.nombre
                      }}</span>
                      <small class="d-block text-muted" style="font-size: 0.73rem">
                        {{ labelPeriodicidad(r.periodicidad) }}
                        <span v-if="r.area_nombre"> · {{ r.area_nombre }}</span>
                        · {{ r.rutina_actividades?.length || 0 }} actividades
                      </small>
                    </div>
                    <!-- Badge periodicidad -->
                    <span class="badge-periodicidad">{{
                      labelPeriodicidad(r.periodicidad)
                    }}</span>
                  </div>
                  <div
                    v-if="!rutinasFiltradas.length"
                    class="custom-dropdown-item text-muted"
                  >
                    Sin rutinas disponibles
                  </div>
                </div>

                <div v-if="form.rutinas.length" class="mt-2">
                  <small class="text-verde fw-bold">
                    <i class="bi bi-check-circle me-1"></i>
                    {{ form.rutinas.length }} rutina(s) seleccionada(s)
                  </small>
                </div>
              </div>

              <!-- Resumen de rutinas seleccionadas (periodicidades distintas) -->
              <div v-if="rutinasSeleccionadas.length > 0" class="col-12">
                <div class="info-rutina">
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <div v-for="r in rutinasSeleccionadas" :key="r.id" class="rut-chip">
                      <span>{{ r.nombre }}</span>
                      <span class="badge-periodicidad ms-1">{{
                        labelPeriodicidad(r.periodicidad)
                      }}</span>
                    </div>
                  </div>
                  <small class="text-cafe opacity-75 d-block mt-2">
                    <i class="bi bi-info-circle me-1"></i>
                    Las fechas en fin de semana se mueven al lunes siguiente. Cada rutina
                    genera sus propias fechas según su periodicidad.
                  </small>
                </div>
              </div>

              <!-- Día ancla detectado (la fecha de inicio ya se eligió arriba) -->
              <div class="col-12">
                <!-- Indicador del día ancla detectado -->
                <div v-if="diaAnclaInfo" class="dia-ancla-chip mt-2">
                  <i class="bi bi-pin-angle-fill me-1"></i>
                  Día ancla detectado: <strong>{{ diaAnclaInfo.nombre }}</strong>
                  — todas las rutinas se programarán en
                  <strong>{{ diaAnclaInfo.nombre }}s</strong>
                  <span v-if="diaAnclaInfo.esFinde" class="ms-1 text-warning-dark">
                    (fin de semana → se moverá al lunes)
                  </span>
                </div>
                <small class="text-cafe opacity-60 d-block mt-1">
                  Se programará hasta el {{ finAnioStr }}. El día de la semana elegido se
                  mantiene en todas las ocurrencias.
                </small>
              </div>

              <!-- Preview agrupado -->
              <div v-if="resumenFechas.totalNuevas > 0" class="col-12">
                <div class="preview-header">
                  <i class="bi bi-calendar-check me-2"></i>
                  Se crearán <strong>{{ resumenFechas.totalNuevas }}</strong> evento(s) en
                  <strong>{{ resumenFechas.totalFechas }}</strong> fecha(s)
                  <span
                    v-if="resumenFechas.totalConflictos > 0"
                    class="ms-2 text-warning-dark"
                  >
                    · {{ resumenFechas.totalConflictos }} ya programados (se omitirán)
                  </span>
                </div>
                <!-- Lista de fechas únicas con qué rutinas van ese día -->
                <div class="preview-lista">
                  <div
                    v-for="(rutIds, fecha) in previewPorFecha"
                    :key="fecha"
                    class="preview-item"
                  >
                    <i class="bi bi-check-circle-fill text-verde me-2"></i>
                    <strong>{{ formatFechaPreview(fecha) }}</strong>
                    <span class="ms-2 text-muted" style="font-size: 0.75rem">
                      {{
                        rutIds
                          .map((id) => rutinas.find((r) => r.id === id)?.nombre || id)
                          .join(", ")
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── PASO 2: Progreso ── -->
          <div v-if="paso === 2" class="progreso-container">
            <div class="text-center mb-4">
              <div v-if="store.progresoRecurrente.activo">
                <div
                  class="spinner-border text-verde mb-3"
                  style="width: 3rem; height: 3rem"
                ></div>
                <h6 class="fw-bold text-cafe">Programando eventos...</h6>
              </div>
              <div v-else>
                <i class="bi bi-check-circle-fill text-verde" style="font-size: 3rem"></i>
                <h6 class="fw-bold text-cafe mt-2">¡Programación completada!</h6>
              </div>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-cafe fw-bold">Progreso</small>
                <small class="text-cafe fw-bold"
                  >{{ store.progresoRecurrente.creados }} /
                  {{ store.progresoRecurrente.total }}</small
                >
              </div>
              <div class="progress" style="height: 10px; border-radius: 8px">
                <div
                  class="progress-bar bg-verde"
                  role="progressbar"
                  :style="{ width: porcentajeProgreso + '%' }"
                ></div>
              </div>
            </div>

            <div v-if="!store.progresoRecurrente.activo" class="resultado-grid">
              <div class="resultado-item exito">
                <i class="bi bi-check-circle-fill"></i>
                <span class="resultado-num">{{ resultadoFinal.creados }}</span>
                <span class="resultado-label">Creados</span>
              </div>
              <div
                class="resultado-item omitido"
                title="Ya estaban programadas en ese período"
              >
                <i class="bi bi-skip-forward-fill"></i>
                <span class="resultado-num">{{ resultadoFinal.yaExistian }}</span>
                <span class="resultado-label">Ya programadas</span>
              </div>
              <div
                v-if="store.progresoRecurrente.errores.length"
                class="resultado-item error"
              >
                <i class="bi bi-exclamation-triangle-fill"></i>
                <span class="resultado-num">{{
                  store.progresoRecurrente.errores.length
                }}</span>
                <span class="resultado-label">Con error</span>
              </div>
            </div>

            <div
              v-if="
                store.progresoRecurrente.errores.length &&
                !store.progresoRecurrente.activo
              "
              class="errores-lista mt-3"
            >
              <small class="text-danger fw-bold d-block mb-1"
                >Fechas que no se pudieron crear:</small
              >
              <div
                v-for="fecha in store.progresoRecurrente.errores"
                :key="fecha"
                class="error-item"
              >
                <i class="bi bi-x-circle-fill text-danger me-1"></i>{{ fecha }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <template v-if="paso === 1">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              data-bs-dismiss="modal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-nuevo py-2 px-4"
              @click="confirmar"
              :disabled="!puedeConfirmar"
            >
              <i class="bi bi-arrow-right me-1"></i>
              Confirmar y programar ({{ resumenFechas.totalNuevas }})
            </button>
          </template>
          <template v-if="paso === 2">
            <button
              type="button"
              class="btn btn-nuevo py-2 px-4"
              @click="cerrar"
              :disabled="store.progresoRecurrente.activo"
            >
              <i class="bi bi-check me-1"></i>Aceptar
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import {
  useCalendarioStore,
  calcularFechasRecurrentes,
  LABEL_PERIODICIDAD,
} from "@/store/calendarioStore";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";

const props = defineProps({
  maquinas: { type: Array, default: () => [] },
  rutinas: { type: Array, default: () => [] },
});

const emit = defineEmits(["completado"]);

const store = useCalendarioStore();
const modalEl = ref(null);
let modalInstance = null;

const paso = ref(1);
const resultadoFinal = ref({ creados: 0, yaExistian: 0 });
const form = ref({ maquina: "", rutinas: [], fechaInicio: "" });
const busquedaRutina = ref("");

const hoy = new Date();
const hoyStr = hoy.toISOString().split("T")[0];
const finAnio = new Date(hoy.getFullYear(), 11, 31);
const finAnioStr = finAnio.toISOString().split("T")[0];

// ── Directiva click-outside ──────────────────────────────────
const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};

// ── Dropdowns ────────────────────────────────────────────────
const dropMaquina = ref(false);
function cerrarDropMaquina() {
  dropMaquina.value = false;
}
function elegirMaquina(id) {
  form.value.maquina = id;
  dropMaquina.value = false;
}

function toggleRutina(id) {
  const idx = form.value.rutinas.indexOf(id);
  if (idx === -1) form.value.rutinas.push(id);
  else form.value.rutinas.splice(idx, 1);
}

// ── Computed ─────────────────────────────────────────────────
const rutinasFiltradas = computed(() => {
  const q = busquedaRutina.value.toLowerCase();
  if (!q) return props.rutinas;
  return props.rutinas.filter(
    (r) =>
      r.nombre.toLowerCase().includes(q) ||
      (r.area_nombre || "").toLowerCase().includes(q)
  );
});

const rutinasSeleccionadas = computed(() =>
  props.rutinas.filter((r) => form.value.rutinas.includes(r.id))
);

// Día de semana detectado de la fecha de inicio (ancla automática)
const NOMBRES_DIA = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];
const diaAnclaInfo = computed(() => {
  if (!form.value.fechaInicio) return null;
  const d = new Date(form.value.fechaInicio + "T12:00:00");
  const diaSemana = d.getDay(); // 0=dom … 6=sáb
  return {
    numero: diaSemana,
    nombre: NOMBRES_DIA[diaSemana],
    esFinde: diaSemana === 0 || diaSemana === 6,
  };
});

// Preview: Map<fecha, rutinaId[]> de fechas nuevas (sin conflicto)
const previewPorFecha = computed(() => {
  if (!form.value.fechaInicio || !rutinasSeleccionadas.value.length) return {};
  const mapa = {};
  for (const rut of rutinasSeleccionadas.value) {
    const fechas = calcularFechasRecurrentes(
      form.value.fechaInicio,
      rut.periodicidad,
      hoy.getFullYear()
    );
    for (const fecha of fechas) {
      const conflicto = store._buscarConflictoPeriodo(
        form.value.maquina,
        rut.id,
        rut.periodicidad,
        fecha
      );
      if (!conflicto) {
        if (!mapa[fecha]) mapa[fecha] = [];
        mapa[fecha].push(rut.id);
      }
    }
  }
  // Ordenar las keys
  return Object.fromEntries(Object.entries(mapa).sort(([a], [b]) => a.localeCompare(b)));
});

const resumenFechas = computed(() => {
  if (!form.value.fechaInicio || !rutinasSeleccionadas.value.length) {
    return { totalFechas: 0, totalNuevas: 0, totalConflictos: 0 };
  }
  let totalFechas = 0,
    totalNuevas = 0,
    totalConflictos = 0;
  for (const rut of rutinasSeleccionadas.value) {
    const fechas = calcularFechasRecurrentes(
      form.value.fechaInicio,
      rut.periodicidad,
      hoy.getFullYear()
    );
    totalFechas += fechas.length;
    for (const fecha of fechas) {
      const conflicto = store._buscarConflictoPeriodo(
        form.value.maquina,
        rut.id,
        rut.periodicidad,
        fecha
      );
      if (conflicto) totalConflictos++;
      else totalNuevas++;
    }
  }
  return {
    totalFechas: Object.keys(previewPorFecha.value).length,
    totalNuevas,
    totalConflictos,
  };
});

const puedeConfirmar = computed(
  () =>
    form.value.maquina &&
    form.value.rutinas.length > 0 &&
    form.value.fechaInicio &&
    resumenFechas.value.totalNuevas > 0
);

const porcentajeProgreso = computed(() => {
  const { total, creados } = store.progresoRecurrente;
  if (!total) return 0;
  return Math.round((creados / total) * 100);
});

// ── Helpers ──────────────────────────────────────────────────
const labelPeriodicidad = (v) => LABEL_PERIODICIDAD[v] || v || "";

function formatFechaPreview(fecha) {
  return new Date(fecha + "T12:00:00").toLocaleDateString("es-MX", {
    weekday: "short",
    day: "numeric",
    month: "long",
  });
}

// ── Métodos ──────────────────────────────────────────────────
function abrir() {
  paso.value = 1;
  form.value = { maquina: "", rutinas: [], fechaInicio: hoyStr };
  resultadoFinal.value = { creados: 0, yaExistian: 0 };
  dropMaquina.value = false;
  busquedaRutina.value = "";
  modalInstance?.show();
}

function cerrar() {
  if (store.progresoRecurrente.activo) return;
  modalInstance?.hide();
  if (paso.value === 2) emit("completado");
}

async function confirmar() {
  if (!puedeConfirmar.value) return;

  const maquinaNombre =
    props.maquinas.find((m) => m.id === form.value.maquina)?.nombre || "";

  const confirmacion = await Swal.fire({
    icon: "question",
    title: "¿Confirmar programación?",
    html: `Se crearán <strong>${resumenFechas.value.totalNuevas}</strong> evento(s) para
           <strong>${maquinaNombre}</strong>
           con <strong>${form.value.rutinas.length}</strong> rutina(s)
           hasta el 31 de diciembre de ${hoy.getFullYear()}.
           ${
             resumenFechas.value.totalConflictos > 0
               ? `<br><small class="text-muted">${resumenFechas.value.totalConflictos} ya programados serán omitidos.</small>`
               : ""
           }
           <br><br><small>Las fechas en fin de semana se mueven al lunes siguiente.</small>`,
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Sí, programar",
    cancelButtonText: "Revisar",
  });

  if (!confirmacion.isConfirmed) return;

  paso.value = 2;

  const resultado = await store.programarRecurrente(
    form.value.maquina,
    form.value.rutinas,
    rutinasSeleccionadas.value,
    form.value.fechaInicio
  );

  resultadoFinal.value = {
    creados: resultado.creados,
    yaExistian: resultado.yaExistian,
  };
}

onMounted(() => {
  modalInstance = new Modal(modalEl.value);
});
defineExpose({ abrir, cerrar });
</script>

<style scoped>
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  border-radius: 8px;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-nuevo:disabled {
  opacity: 0.5;
}

/* Día ancla chip */
.dia-ancla-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(64, 34, 8, 0.08);
  border: 1px solid rgba(64, 34, 8, 0.2);
  color: var(--d-brown);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Dropdown máquina */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
  cursor: pointer;
  user-select: none;
  min-height: 38px;
  display: flex;
  align-items: center;
  color: var(--d-brown);
}
.placeholder-text {
  color: #6c757d;
}
.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer;
  pointer-events: auto;
}
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1080;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
  max-height: 185px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
  color: var(--d-brown);
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}

/* Multi-select rutinas */
.search-rutina {
  position: relative;
}
.search-rutina i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
}

.rutinas-lista {
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
}
.rutina-check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.rutina-check-row:last-child {
  border-bottom: none;
}
.rutina-check-row:hover {
  background: rgba(96, 108, 56, 0.06);
}
.rutina-check-row.seleccionada {
  background: rgba(96, 108, 56, 0.1);
}
.rutina-check-box {
  font-size: 1.1rem;
  color: var(--d-green);
  flex-shrink: 0;
}
.rutina-check-row.seleccionada .rutina-check-box {
  color: var(--d-green-dark);
}

/* Info rutina seleccionada */
.info-rutina {
  background: rgba(96, 108, 56, 0.08);
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 10px;
  padding: 0.75rem 1rem;
}
.rut-chip {
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--d-brown);
  gap: 4px;
}

.badge-periodicidad {
  background: rgba(64, 34, 8, 0.1);
  color: var(--d-brown);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

/* Preview */
.preview-header {
  background: rgba(96, 108, 56, 0.1);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 10px 10px 0 0;
  padding: 0.6rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--d-brown);
}
.text-warning-dark {
  color: #856404;
}
.preview-lista {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-top: none;
  border-radius: 0 0 10px 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
}
.preview-item {
  font-size: 0.82rem;
  color: var(--d-brown);
  padding: 4px 6px;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
}
.preview-item:hover {
  background: rgba(96, 108, 56, 0.06);
}

/* Progreso */
.progreso-container {
  min-height: 200px;
}
.bg-verde {
  background-color: var(--d-green) !important;
}
.progress {
  background-color: rgba(96, 108, 56, 0.15);
}

.resultado-grid {
  display: flex;
  gap: 12px;
}
.resultado-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  border-radius: 12px;
}
.resultado-item.exito {
  background: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
}
.resultado-item.omitido {
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
}
.resultado-item.error {
  background: rgba(204, 54, 54, 0.1);
  color: #7f1d1d;
}
.resultado-num {
  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1;
  margin: 4px 0;
}
.resultado-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.errores-lista {
  background: rgba(204, 54, 54, 0.06);
  border: 1px solid rgba(204, 54, 54, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
}
.error-item {
  font-size: 0.82rem;
  color: #7f1d1d;
  padding: 2px 0;
}
</style>
